<template>
  <div>
    <v-row>
      <v-col cols="12">
        <p>{{ $t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signatureTypeCaption')}}</p>
        <div>
          <AppDownloadFile :link="learnMoreSignatureTypesLink" forceReader class="app-link learnMoreLink">
            <template #default="{ submitForm }">
              <span @click="submitForm">
                {{ $t("project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.learnMoreSignatureType") }}
                <app-material-icon name="open_in_new" size="xs" color="inherit" />
              </span>
            </template>
          </AppDownloadFile>
        </div>
      </v-col>

      <v-col cols="12">
        <app-alert-in-page
          v-if="signatureTypeErrorMessages.length"
          variant="error"
          :dismissible="false"
          class="mb-4"
          data-locator="signature-type-error-alert"
        >
          <p v-for="message in signatureTypeErrorMessages" :key="message" class="text--small">
            {{ message }}
          </p>
        </app-alert-in-page>
        <app-tile-group
          :model-value="signatureType"
          :options="signatureTypeTileOptions"
          :disabled="isEditingLimited"
          :aria-label="$t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signatureTypeLabel')"
          data-locator="signature-type-tile-group"
          @update:model-value="signatureTypeChange"
        />
      </v-col>
    </v-row>

    <v-row v-if="signatureType === SIGNATURE_TYPE_HANDWRITTEN">
      <v-col>
        <v-checkbox
          :model-value="hasMercuryOptionActivated"
          class="mt-0"
          density="compact"
          hide-details
          :label="$t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.mercuryCaseLabel')"
          @update:model-value="onMercuryOptionChange"
        ></v-checkbox>
      </v-col>
    </v-row>

    <SignerOrderSignatureTypeIncompatibilityWarningDialog
      v-if="signerOrderSignatureTypeIncompatibilityWarningDialog"
      @close="signerOrderSignatureTypeIncompatibilityWarningDialog = false"
      @confirm="confirmChangeSignatureType"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapMutations, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import { formatFileSize } from '@/common/utils/files'
import { formFieldErrors, VuelidateFormField } from '@/common/utils/form'
import { BtoMB } from '@/common/utils/sizes'
import type { TileOption } from '@/design-system/tiles/types'
import {
  SIGNATURE_TYPE_DISPLAY_ORDER,
  SIGNATURE_TYPE_HANDWRITTEN,
  SIGNATURE_TYPE_WITH_AUTH,
} from '@/project/signing-checklist/constants'
import SignerOrderSignatureTypeIncompatibilityWarningDialog from '@/project/signing-checklist/dialogs/SignerOrderSignatureTypeIncompatibilityWarningDialog.vue'
import { SET_IMPORTED_FILE_MERCURY_OPTION } from '@/store/modules/signing-checklist/mutation_types'

type EnvelopeTypeOption = {
  disabled: boolean
  providerMaxSize: number
  text: string
  value: string
}

export default defineComponent({
  name: 'SigningChecklistSectionDocumentSignatureType',
  components: { AppDownloadFile, SignerOrderSignatureTypeIncompatibilityWarningDialog },
  props: {
    signatureType: {
      type: String,
      required: true,
    },
    isEditingLimited: {
      type: Boolean,
      required: true,
    },
    isHandwrittenSignatureDisabled: {
      type: Boolean,
      required: false,
    },
    signatureTypeValidator: {
      type: Object as PropType<VuelidateFormField>,
      required: true,
    },
    fileSize: {
      type: Number,
      required: true,
    },
    hasSignersOrder: {
      type: Boolean,
      required: true,
    },
    hasMercuryOptionActivated: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      SIGNATURE_TYPE_HANDWRITTEN,
      signerOrderSignatureTypeIncompatibilityWarningDialog: false,
    }
  },
  computed: {
    ...mapState('signingChecklist', ['qualifiedDSWarningIsMuted']),
    ...mapState('room', ['currentRoom', 'roomProvidersPending']),
    learnMoreSignatureTypesLink (): string {
      const locale = this.$i18n.locale as string
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=signatures-types-${locale.toUpperCase()}`
    },
    signatureTypeErrorMessages () {
      return formFieldErrors(
        this.signatureTypeValidator,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signatureTypeLabel') })],
        ],
      )
    },
    envelopeTypeOptions () {
      const envelopesTypesOptions: Record<string, EnvelopeTypeOption> = {}
      if (!this.roomProvidersPending && this.currentRoom.providers) {
        for (const provider of this.currentRoom.providers) {
          for (const type of provider.types) {
            const value = provider.provider + '.' + type

            if (this.isHandwrittenSignatureDisabled && value === SIGNATURE_TYPE_HANDWRITTEN) continue

            envelopesTypesOptions[value] = {
              text: this.$t(`project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signingTypeSelect.${value}`),
              value,
              disabled: this.fileSize > provider.maxSize,
              providerMaxSize: BtoMB(provider.maxSize),
            }
          }
        }
      }

      return SIGNATURE_TYPE_DISPLAY_ORDER
        .map(signatureType => envelopesTypesOptions[signatureType])
        .filter(e => e !== undefined)
    },
    signatureTypeTileOptions (): TileOption[] {
      return this.envelopeTypeOptions.map(option => ({
        value: option.value,
        label: option.text,
        caption: this.getTileCaption(option),
        disabled: option.disabled,
        tooltip: option.disabled
          ? this.$t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.envelopeTypeOptionDisabledTooltip')
          : undefined,
        dataLocator: `${option.value.split('.').join('-')}-signature-type-card`,
      }))
    },
  },
  methods: {
    ...mapMutations('signingChecklist', [SET_IMPORTED_FILE_MERCURY_OPTION]),
    signatureTypeChange (signatureType: string | null) {
      const shouldWarnAboutManualSignature = signatureType === SIGNATURE_TYPE_HANDWRITTEN && this.hasSignersOrder
      if (shouldWarnAboutManualSignature) {
        this.signerOrderSignatureTypeIncompatibilityWarningDialog = true
        return
      }
      const shouldWarnAboutQualifiedSignature = signatureType === 'ds.qualified' && !this.qualifiedDSWarningIsMuted

      if (shouldWarnAboutQualifiedSignature) {
        this.$emit('setQualifiedDSWarningDialog', true)
        this.$emit('signature-type-change', this.signatureType)
      } else {
        this.$emit('signature-type-change', signatureType)
      }
    },
    confirmChangeSignatureType () {
      this.signerOrderSignatureTypeIncompatibilityWarningDialog = false
      this.$emit('signature-type-change', SIGNATURE_TYPE_HANDWRITTEN)
    },
    getProviderMaxSizeInBytes (providerMaxSize: number): number {
      return providerMaxSize * 1048576
    },
    getTileCaption (option: EnvelopeTypeOption): string {
      const parts: string[] = []
      if (SIGNATURE_TYPE_WITH_AUTH.includes(option.value)) {
        parts.push(this.$t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signingTypeAuthRequired'))
      }
      if (option.value === SIGNATURE_TYPE_HANDWRITTEN) {
        parts.push(this.$t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signingTypeNoOrder'))
      }
      parts.push(this.$t(
        'project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signingTypeMaxSize',
        [formatFileSize(this.getProviderMaxSizeInBytes(option.providerMaxSize))],
      ))
      return parts.join(' ')
    },
    onMercuryOptionChange (value: boolean | null) {
      this.$emit('mercury-option-change', value)
    },
  },
})
</script>

<style scoped lang="scss">
.learnMoreLink {
  color: #4C75B5;
}
</style>
